export const NOTIFICATION_CENTER_PATH_NAME = 'notification-center';

export const NOTIFICATION_CENTER_PAGINATION = 50;

export const NOTIFICATION_KIND = {
  ACTION: 'ACTION',
  HEALTH_ISSUE: 'HEALTH_ISSUE',
  CONFIG_GAP: 'CONFIG_GAP',
} as const;

export type NotificationKind =
  (typeof NOTIFICATION_KIND)[keyof typeof NOTIFICATION_KIND];

export const NOTIFICATION_SOURCE = {
  PLATFORM: 'PLATFORM',
  TORCH: 'TORCH',
} as const;

export type NotificationSource =
  (typeof NOTIFICATION_SOURCE)[keyof typeof NOTIFICATION_SOURCE];

export const NOTIFICATION_STATUS = {
  OPEN: 'OPEN',
  RESOLVED: 'RESOLVED',
} as const;

export type NotificationStatus =
  (typeof NOTIFICATION_STATUS)[keyof typeof NOTIFICATION_STATUS];

export const NOTIFICATION_ENTITY_TYPE = {
  FINDING: 'FINDING',
  CREDENTIAL: 'CREDENTIAL',
  DOMAIN: 'DOMAIN',
  CONNECTOR: 'CONNECTOR',
  REPOSITORY: 'REPOSITORY',
  KNOWLEDGE_SOURCE: 'KNOWLEDGE_SOURCE',
  APPLICATION: 'APPLICATION',
  MEMORY: 'MEMORY',
  IMPORT_BATCH: 'IMPORT_BATCH',
} as const;

export type NotificationEntityType =
  (typeof NOTIFICATION_ENTITY_TYPE)[keyof typeof NOTIFICATION_ENTITY_TYPE];

export const NOTIFICATION_RESOLUTION_REASON = {
  FINDING_STATUS_CHANGED: 'findingStatusChanged',
  FINDING_UNPUBLISHED: 'findingUnpublished',
  REMEDIATION_PLAN_CREATED: 'remediationPlanCreated',
  REMEDIATION_PR_CREATED: 'remediationPrCreated',
  REMEDIATION_PR_MERGED: 'remediationPrMerged',
  RETEST_STARTED: 'retestStarted',
  ENTITY_VIEWED: 'entityViewed',
  HEALTH_ISSUE_CLEARED: 'healthIssueCleared',
  CONFIG_GAP_RESOLVED: 'configGapResolved',
  SIGNAL_IMPORT_ACKNOWLEDGED: 'signalImportAcknowledged',
} as const;

export type NotificationResolutionReason =
  (typeof NOTIFICATION_RESOLUTION_REASON)[keyof typeof NOTIFICATION_RESOLUTION_REASON];

export const PLATFORM_NOTIFICATION_ACTION_TYPE = {
  TRIAGE_NEW: 'platform.finding.triageNew',
  CONTINUE_REMEDIATION: 'platform.finding.continueRemediation',
  REVIEW_RETEST_STILL_VULNERABLE:
    'platform.finding.reviewRetestStillVulnerable',
  INVESTIGATE_RETEST_FAILURE: 'platform.finding.investigateRetestFailure',
  SEVERITY_CHANGE_APPROVED: 'platform.finding.severityChangeApproved',
  SEVERITY_CHANGE_REJECTED: 'platform.finding.severityChangeRejected',
  REVIEW_NEW_MEMORY: 'platform.memory.reviewNew',
  NEW_SCHEDULED_REPORT: 'platform.report.newScheduledReport',
  BUG_BOUNTY_SIGNAL_IMPORT_SUMMARY: 'platform.signal.bugBountyImportSummary',
  SIGNAL_IMPORT_COMPLETED: 'platform.signalImport.importCompleted',
  INITIAL_TESTING_COMPLETE: 'platform.application.initialTestingComplete',
} as const;

export type PlatformNotificationActionType =
  (typeof PLATFORM_NOTIFICATION_ACTION_TYPE)[keyof typeof PLATFORM_NOTIFICATION_ACTION_TYPE];

export const PLATFORM_NOTIFICATION_HEALTH_TYPE = {
  CREDENTIAL_VERIFICATION_FAILED:
    'platform.health.credentialVerificationFailed',
  WAF_BLOCKING_APPLICATION_URL: 'platform.health.wafBlockingApplicationUrl',
  WAF_BLOCKING_ADDITIONAL_DOMAIN: 'platform.health.wafBlockingAdditionalDomain',
  INTERNAL_CONNECTOR_OFFLINE: 'platform.health.internalConnectorOffline',
  INTERNAL_CONNECTOR_REQUIRED: 'platform.health.internalConnectorRequired',
  SOURCE_CODE_INTEGRATION_UNREACHABLE:
    'platform.health.sourceCodeIntegrationUnreachable',
  SOURCE_CODE_REPOSITORIES_UNREACHABLE:
    'platform.health.sourceCodeRepositoriesUnreachable',
  SOURCE_CODE_CONNECTOR_OFFLINE: 'platform.health.sourceCodeConnectorOffline',
  MISSING_API_SPECIFICATION: 'platform.health.missingApiSpecification',
  NO_SIGNAL_CATEGORIES_IN_SCOPE: 'platform.health.noSignalCategoriesInScope',
  KNOWLEDGE_SOURCE_SYNC_FAILED: 'platform.health.knowledgeSourceSyncFailed',
  CLOUD_IP_UNASSOCIATED: 'platform.health.cloudIpUnassociated',
  ADAPTIVE_SCANNING_PAUSED: 'platform.health.adaptiveScanningPaused',
} as const;

export type PlatformNotificationHealthType =
  (typeof PLATFORM_NOTIFICATION_HEALTH_TYPE)[keyof typeof PLATFORM_NOTIFICATION_HEALTH_TYPE];

export const PLATFORM_NOTIFICATION_CONFIG_GAP_TYPE = {
  NO_CREDENTIALS_CONFIGURED: 'platform.config.noCredentialsConfigured',
  ALL_CREDENTIALS_DISABLED: 'platform.config.allCredentialsDisabled',
  NO_SOURCE_CODE_INTEGRATION: 'platform.config.noSourceCodeIntegration',
  NO_REPOSITORIES_LINKED: 'platform.config.noRepositoriesLinked',
  NO_API_DOCUMENTATION: 'platform.config.noApiDocumentation',
  NO_APPLICATION_DOCUMENTATION: 'platform.config.noApplicationDocumentation',
  NO_KNOWLEDGE_BASE_INTEGRATION: 'platform.config.noKnowledgeBaseIntegration',
  NO_KNOWLEDGE_SOURCE_PAGES_MAPPED:
    'platform.config.noKnowledgeSourcePagesMapped',
  NO_PREVIOUS_TESTING_REPORTS: 'platform.config.noPreviousTestingReports',
} as const;

export type PlatformNotificationConfigGapType =
  (typeof PLATFORM_NOTIFICATION_CONFIG_GAP_TYPE)[keyof typeof PLATFORM_NOTIFICATION_CONFIG_GAP_TYPE];

export const CONFIG_COMPLETENESS_BASELINE_SCORE = 37;
export const CONFIG_COMPLETENESS_POINTS_PER_DIMENSION = 7;
export const CONFIG_COMPLETENESS_TOTAL_DIMENSIONS = 9;

export const computeConfigCompletenessScore = ({
  activeGapCount,
}: {
  activeGapCount: number;
}): number =>
  CONFIG_COMPLETENESS_BASELINE_SCORE +
  CONFIG_COMPLETENESS_POINTS_PER_DIMENSION *
    (CONFIG_COMPLETENESS_TOTAL_DIMENSIONS - activeGapCount);

export const NOTIFICATION_PRIORITY_TIER = {
  P1: 1,
  P2: 2,
  P3: 3,
} as const;

export type NotificationPriorityTier =
  (typeof NOTIFICATION_PRIORITY_TIER)[keyof typeof NOTIFICATION_PRIORITY_TIER];

export const FINDING_SEVERITY_RANK = {
  Critical: 5,
  High: 4,
  Medium: 3,
  Low: 2,
  Info: 1,
} as const;

export type FindingSeverityRankKey = keyof typeof FINDING_SEVERITY_RANK;
